import type { MouseEvent as ReactMouseEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { CaretRightIcon } from '@phosphor-icons/react';
import type { EventCardProps } from '@/lib/types/events.types';
import { EventFileCountBadge } from '@/components/events/EventFileCountBadge';
import {
  formatEventDateTime,
  getRsvpStatusText,
  getRsvpStatusColor,
} from '@/lib/utils/events.utils';
import { cn } from '@/lib/utils';

export function EventCard({
  event,
  rsvpStatus,
  fileCount = 0,
  yGrow = false,
  isPast = false,
  onNavigate,
  seriesInfo,
}: EventCardProps) {
  const statusText = getRsvpStatusText(rsvpStatus);
  const statusColor = getRsvpStatusColor(rsvpStatus);

  const handleClick = (eventClick: ReactMouseEvent<HTMLElement>) => {
    if (eventClick.defaultPrevented) {
      return;
    }

    if (eventClick.button !== 0) {
      return;
    }

    if (
      eventClick.metaKey ||
      eventClick.altKey ||
      eventClick.ctrlKey ||
      eventClick.shiftKey
    ) {
      return;
    }

    onNavigate?.();
  };

  return (
    <Link
      to="/events/$eventId"
      params={{ eventId: String(event.id) }}
      className={cn('block', yGrow && 'h-full')}
      data-event-card-id={event.id}
      data-testid="event-card"
      onClick={handleClick}
    >
      <Card
        className={cn(
          'bg-slate-600 transition-colors cursor-pointer',
          yGrow && 'h-full'
        )}
      >
        <CardContent className={cn('p-4 text-slate-50', yGrow && 'h-full')}>
          <div
            className={cn(
              'flex items-start justify-between gap-4',
              yGrow && 'h-full'
            )}
          >
            <div
              className={cn('flex-1 min-w-0', yGrow && 'flex flex-col h-full')}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={cn(
                    'font-semibold text-foreground font-field-64 text-large truncate',
                    isPast && 'text-slate-400 line-through'
                  )}
                >
                  {event.title}
                </span>
                {event.series_id && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded whitespace-nowrap">
                    Series
                    {seriesInfo &&
                      ` (${seriesInfo.position}/${seriesInfo.total})`}
                  </span>
                )}
                {isPast && (
                  <span className="text-xs bg-slate-400 text-slate-800 px-2 py-0.5 rounded">
                    Past
                  </span>
                )}
                <EventFileCountBadge count={fileCount} />
              </div>
              {event.location && (
                <p className="text-body text-slate-500 mb-2 font-field-62">
                  {event.location}
                </p>
              )}
              {event.description && (
                <p className="text-body mb-2 line-clamp-2 break-words [overflow-wrap:anywhere]">
                  {event.description}
                </p>
              )}
              <div className={cn('', yGrow && 'mt-auto')} id="lower-section">
                <p className="text-small mb-2 text-slate-400">
                  {formatEventDateTime(event.start_time)}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <div
                    className={`w-2 h-2 rounded-full ${statusColor}`}
                    aria-hidden="true"
                  />
                  <span className="text-sm text-foreground/80">
                    {statusText}
                  </span>
                </div>
              </div>
            </div>
            <CaretRightIcon
              className="flex-shrink-0 text-foreground/60"
              size={20}
              weight="bold"
            />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
